// Form 19 Modal with correct supplier API endpoint (/api/supplier)
import React, { useState, useEffect } from 'react';
import {
  Modal, Form, Input, Select, DatePicker, Button, Tag, Row, Col, Typography, message
} from 'antd';
import moment from 'moment';
import { platformApi } from '../../../../api';

const { Option } = Select;
const { Item } = Form;

const toTitleCase = (str) => {
  if (!str || typeof str !== 'string') return '';
  return str.replace(/\b[a-zA-Z]+/g, (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
};

const Form19Modal = (props) => {
  const { form, open, close, values, mode, onSuccess } = props;
  const { getFieldDecorator, setFieldsValue, resetFields, validateFields, getFieldValue } = form;

  const [loading, setLoading] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [tcTags, setTcTags] = useState([]);
  const [tcInputValue, setTcInputValue] = useState('');

  const isView = mode === 'view';
  const isEdit = mode === 'edit';

  useEffect(() => {
    if (open) {
      fetchSuppliers();
      fetchCountries();
    }
  }, [open]);

  useEffect(() => {
    if (open && values) {
      setTcTags(values.tcNumbers || []);
      const supplierId = values.dealerId || values.supplierId || values.Supplier?.id;
      const countryVal = values.country || values.Supplier?.address?.country?.name || values.Supplier?.country;
      const stateVal = values.state || values.Supplier?.address?.state?.name || values.Supplier?.state;
      const cityVal = values.city || values.Supplier?.address?.district?.name || values.Supplier?.city;

      const addr1 = values.addressLine1 || values.address1 || values.Supplier?.address?.line1 || values.Supplier?.address1 || '';
      const addr2 = values.addressLine2 || values.address2 || values.Supplier?.address?.line2 || values.Supplier?.address2 || '';
      const addr3 = values.addressLine3 || values.address3 || values.Supplier?.address?.line3 || values.Supplier?.address3 || '';
      const loc = values.locality || values.Supplier?.address?.locality || values.Supplier?.locality || '';
      const pin = values.pincode || values.Supplier?.address?.pincode || values.Supplier?.pincode || '';

      setFieldsValue({
        supplierId: supplierId || undefined,
        addressLine1: toTitleCase(addr1),
        addressLine2: toTitleCase(addr2),
        addressLine3: toTitleCase(addr3),
        locality: loc,
        country: countryVal || undefined,
        state: stateVal || undefined,
        city: cityVal || undefined,
        pincode: pin,
        expiryDate: values.expiryDate ? moment(values.expiryDate) : null,
        driverName: toTitleCase(values.driverName || ''),
        licenceNo: values.licenceNo || '',
        validity: values.validity ? moment(values.validity) : null
      });

      if (countryVal) {
        fetchStates(countryVal, stateVal, cityVal);
      }
    } else if (open && !values) {
      resetFields();
      setTcTags([]);
      setStates([]);
      setCities([]);
    }
  }, [open, values]);

  const fetchSuppliers = () => {
    platformApi
      .get('/api/supplier')
      .then((res) => {
        const responseData = res.data?.response;
        const list = Array.isArray(responseData)
          ? responseData
          : responseData?.data?.suppliers || responseData?.data || res.data?.data || [];

        if (Array.isArray(list) && list.length > 0) {
          setSuppliers(list);
        } else {
          fetchSuppliersPost();
        }
      })
      .catch((err) => {
        console.error('Fetch suppliers GET error:', err);
        fetchSuppliersPost();
      });
  };

  const fetchSuppliersPost = () => {
    platformApi
      .post('/api/supplier/get', { page: 1, size: 1000 })
      .then((res) => {
        const responseData = res.data?.response;
        const list =
          responseData?.data?.suppliers ||
          responseData?.suppliers ||
          res.data?.data?.suppliers ||
          res.data?.suppliers ||
          [];
        if (Array.isArray(list)) {
          setSuppliers(list);
        }
      })
      .catch((err) => {
        console.error('Fetch suppliers POST error:', err);
      });
  };

  const fetchCountries = () => {
    platformApi
      .get('/api/csc/country')
      .then((res) => {
        if (res.data && res.data.code === 200) {
          setCountries(res.data.data || []);
        }
      })
      .catch((err) => {
        console.error('Fetch countries error:', err);
      });
  };

  const fetchStates = (countryNameOrId, targetStateName, targetCityName) => {
    const matchedCountry = countries.find(
      (c) => c.name === countryNameOrId || String(c.id) === String(countryNameOrId)
    );
    const countryId = matchedCountry ? matchedCountry.id : countryNameOrId;

    platformApi
      .post('/api/csc/states', { id: countryId })
      .then((res) => {
        if (res.data && res.data.code === 200) {
          const fetchedStates = res.data.data || [];
          setStates(fetchedStates);

          const stateName = targetStateName || (getFieldValue ? getFieldValue('state') : undefined);
          if (stateName) {
            fetchCities(stateName, fetchedStates, targetCityName);
          }
        }
      })
      .catch((err) => {
        console.error('Fetch states error:', err);
      });
  };

  const fetchCities = (stateNameOrId, stateList = states, targetCityName) => {
    const matchedState = stateList.find(
      (s) => s.name === stateNameOrId || String(s.id) === String(stateNameOrId)
    );
    const stateId = matchedState ? matchedState.id : stateNameOrId;

    platformApi
      .post('/api/csc/cities', { id: stateId })
      .then((res) => {
        if (res.data && res.data.code === 200) {
          setCities(res.data.data || []);
          if (targetCityName) {
            setFieldsValue({ city: targetCityName });
          }
        }
      })
      .catch((err) => {
        console.error('Fetch cities error:', err);
      });
  };

  const handleSupplierSelect = (supplierId) => {
    const supplier = suppliers.find((s) => String(s.id) === String(supplierId));
    if (!supplier) return;

    const addr = supplier.address || supplier.Address || supplier;

    const countryVal = addr?.country?.name || addr?.countryName || supplier.country || '';
    const stateVal = addr?.state?.name || addr?.stateName || supplier.state || '';
    const cityVal = addr?.district?.name || addr?.cityName || supplier.city || '';

    setFieldsValue({
      addressLine1: toTitleCase(addr?.line1 || supplier.address1 || ''),
      addressLine2: toTitleCase(addr?.line2 || supplier.address2 || ''),
      addressLine3: toTitleCase(addr?.line3 || supplier.address3 || ''),
      locality: addr?.locality || supplier.locality || '',
      country: countryVal || undefined,
      state: stateVal || undefined,
      city: cityVal || undefined,
      pincode: addr?.pincode || supplier.pincode || ''
    });

    if (countryVal) {
      fetchStates(countryVal, stateVal, cityVal);
    }
  };

  const handleAddTag = () => {
    const trimmed = tcInputValue.trim();
    if (trimmed && !tcTags.includes(trimmed)) {
      setTcTags([...tcTags, trimmed]);
      setTcInputValue('');
    }
  };

  const handleRemoveTag = (removedTag) => {
    setTcTags(tcTags.filter((tag) => tag !== removedTag));
  };

  const handleSubmit = () => {
    validateFields((err, formValues) => {
      if (err) return;

      const supplierId = formValues.supplierId;
      const selectedSupplier = suppliers.find((s) => String(s.id) === String(supplierId));
      const dealerName = selectedSupplier
        ? (selectedSupplier.name || selectedSupplier.supplierName || selectedSupplier.companyName)
        : (values ? values.dealerName : undefined);

      const payload = {
        dealerId: supplierId || null,
        dealerName: dealerName || null,
        addressLine1: toTitleCase(formValues.addressLine1) || null,
        addressLine2: toTitleCase(formValues.addressLine2) || null,
        addressLine3: toTitleCase(formValues.addressLine3) || null,
        locality: formValues.locality || null,
        country: formValues.country || null,
        state: formValues.state || null,
        city: formValues.city || null,
        pincode: formValues.pincode || null,
        tcNumbers: tcTags,
        expiryDate: formValues.expiryDate ? formValues.expiryDate.format('YYYY-MM-DD') : null,
        driverName: toTitleCase(formValues.driverName) || null,
        licenceNo: formValues.licenceNo || null,
        validity: formValues.validity ? formValues.validity.format('YYYY-MM-DD') : null
      };

      setLoading(true);
      if (isEdit && values?.id) {
        platformApi
          .put(`/api/form-19/${values.id}`, payload)
          .then((res) => {
            if (res.data && res.data.code === 200) {
              message.success('Form 19 updated successfully');
              close();
              onSuccess && onSuccess();
            } else {
              message.error(res.data?.message || 'Failed to update Form 19');
            }
          })
          .catch((error) => {
            console.error('Update Form 19 Error:', error);
            message.error('Server error updating Form 19');
          })
          .finally(() => {
            setLoading(false);
          });
      } else {
        platformApi
          .post('/api/form-19', payload)
          .then((res) => {
            if (res.data && res.data.code === 200) {
              message.success('Form 19 created successfully');
              close();
              onSuccess && onSuccess();
            } else {
              message.error(res.data?.message || 'Failed to create Form 19');
            }
          })
          .catch((error) => {
            console.error('Create Form 19 Error:', error);
            message.error('Server error creating Form 19');
          })
          .finally(() => {
            setLoading(false);
          });
      }
    });
  };

  return (
    <Modal
      title="Form 19 Master"
      visible={open}
      width="60%"
      onCancel={() => {
        resetFields();
        setTcTags([]);
        close();
      }}
      footer={
        isView ? [
          <Button key="close" onClick={close}>
            Close
          </Button>
        ] : [
          <Button key="cancel" onClick={close}>
            Cancel
          </Button>,
          <Button key="submit" type="primary" loading={loading} onClick={handleSubmit}>
            {isEdit ? 'Update' : 'Save'}
          </Button>
        ]
      }
      destroyOnClose
    >
      <Form layout="vertical">
        {/* SECTION 1: Dealer Details */}
        <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 15, color: '#595959' }}>
          Dealer Details
        </div>

        {/* Row 1: Dealer Name & Address 1 */}
        <Row gutter={16}>
          <Col span={12}>
            <Item label="Dealer Name :" required>
              {getFieldDecorator('supplierId', {
                rules: [{ required: true, message: 'Please select dealer name!' }]
              })(
                <Select
                  placeholder="Select Dealer Name"
                  disabled={isView}
                  onChange={handleSupplierSelect}
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {suppliers.map((s) => (
                    <Option key={s.id} value={s.id}>
                      {s.name || s.supplierName || s.companyName || s.email}
                    </Option>
                  ))}
                </Select>
              )}
            </Item>
          </Col>
          <Col span={12}>
            <Item label="Address 1 :">
              {getFieldDecorator('addressLine1', {
                getValueFromEvent: (e) => toTitleCase(e.target.value)
              })(
                <Input placeholder="Address 1" disabled={isView} />
              )}
            </Item>
          </Col>
        </Row>

        {/* Row 2: Address 2 & Address 3 */}
        <Row gutter={16}>
          <Col span={12}>
            <Item label="Address 2 :">
              {getFieldDecorator('addressLine2', {
                getValueFromEvent: (e) => toTitleCase(e.target.value)
              })(
                <Input placeholder="Address 2" disabled={isView} />
              )}
            </Item>
          </Col>
          <Col span={12}>
            <Item label="Address 3 :">
              {getFieldDecorator('addressLine3', {
                getValueFromEvent: (e) => toTitleCase(e.target.value)
              })(
                <Input placeholder="Address 3" disabled={isView} />
              )}
            </Item>
          </Col>
        </Row>

        {/* Row 3: Locality & Country */}
        <Row gutter={16}>
          <Col span={12}>
            <Item label="Locality :">
              {getFieldDecorator('locality')(
                <Input placeholder="Locality" disabled={isView} />
              )}
            </Item>
          </Col>
          <Col span={12}>
            <Item label="Country :">
              {getFieldDecorator('country')(
                <Select
                  placeholder="Select Country"
                  disabled={isView}
                  showSearch
                  optionFilterProp="children"
                  onChange={(val, option) => {
                    setStates([]);
                    setCities([]);
                    setFieldsValue({ state: undefined, city: undefined });
                    if (option && option.key) {
                      fetchStates(option.key);
                    }
                  }}
                  filterOption={(input, option) =>
                    option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {countries.map((c) => (
                    <Option key={c.id} value={c.name}>
                      {c.name}
                    </Option>
                  ))}
                </Select>
              )}
            </Item>
          </Col>
        </Row>

        {/* Row 4: State, City, Pin Code */}
        <Row gutter={16}>
          <Col span={8}>
            <Item label="State :">
              {getFieldDecorator('state')(
                <Select
                  placeholder="Select State"
                  disabled={isView}
                  showSearch
                  optionFilterProp="children"
                  onChange={(val, option) => {
                    setCities([]);
                    setFieldsValue({ city: undefined });
                    if (option && option.key) {
                      fetchCities(option.key);
                    }
                  }}
                  filterOption={(input, option) =>
                    option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {states.map((s) => (
                    <Option key={s.id} value={s.name}>
                      {s.name}
                    </Option>
                  ))}
                </Select>
              )}
            </Item>
          </Col>
          <Col span={8}>
            <Item label="City :">
              {getFieldDecorator('city')(
                <Select
                  placeholder="Select City"
                  disabled={isView}
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {cities.map((c) => (
                    <Option key={c.id} value={c.name}>
                      {c.name}
                    </Option>
                  ))}
                </Select>
              )}
            </Item>
          </Col>
          <Col span={8}>
            <Item label="Pin Code :">
              {getFieldDecorator('pincode')(
                <Input placeholder="Pin Code" disabled={isView} />
              )}
            </Item>
          </Col>
        </Row>

        {/* SECTION 2: Trade Certificate Number / Vehicle Registered Number */}
        <div style={{ fontSize: 16, fontWeight: 600, marginTop: 20, marginBottom: 15, color: '#595959' }}>
          Trade Certificate Number / Vehicle Registered Number
        </div>

        <Row gutter={16}>
          <Col span={16}>
            <Item label="TC / Registered Numbers :">
              {!isView && (
                <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                  <Input
                    placeholder="Enter TC / Reg Number & Click Add"
                    value={tcInputValue}
                    onChange={(e) => setTcInputValue(e.target.value)}
                    onPressEnter={(e) => {
                      e.preventDefault();
                      handleAddTag();
                    }}
                  />
                  <Button type="primary" onClick={handleAddTag}>
                    Add
                  </Button>
                </div>
              )}
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '8px',
                  minHeight: '38px',
                  padding: '6px',
                  border: '1px solid #d9d9d9',
                  borderRadius: '4px',
                  backgroundColor: '#fafafa'
                }}
              >
                {tcTags.length === 0 ? (
                  <span style={{ color: '#bfbfbf', fontSize: '12px', alignSelf: 'center' }}>
                    No TC numbers added
                  </span>
                ) : (
                  tcTags.map((tag) => (
                    <Tag
                      key={tag}
                      closable={!isView}
                      onClose={() => handleRemoveTag(tag)}
                      style={{
                        backgroundColor: '#6c757d',
                        color: '#ffffff',
                        fontWeight: '500',
                        fontSize: '13px',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      {tag}
                    </Tag>
                  ))
                )}
              </div>
            </Item>
          </Col>
          <Col span={8}>
            <Item label="Expiry Date :">
              {getFieldDecorator('expiryDate')(
                <DatePicker
                  style={{ width: '100%' }}
                  format="DD-MM-YYYY"
                  disabled={isView}
                  placeholder="Select Expiry Date"
                />
              )}
            </Item>
          </Col>
        </Row>

        {/* SECTION 3: Driving Licence Section */}
        <div style={{ fontSize: 16, fontWeight: 600, marginTop: 20, marginBottom: 15, color: '#595959' }}>
          Driving Licence Details
        </div>

        <Row gutter={16}>
          <Col span={8}>
            <Item label="Driver Name :">
              {getFieldDecorator('driverName', {
                getValueFromEvent: (e) => toTitleCase(e.target.value)
              })(
                <Input placeholder="Driver Name" disabled={isView} />
              )}
            </Item>
          </Col>
          <Col span={8}>
            <Item label="Licence No :">
              {getFieldDecorator('licenceNo')(
                <Input placeholder="Licence No" disabled={isView} />
              )}
            </Item>
          </Col>
          <Col span={8}>
            <Item label="Validity :">
              {getFieldDecorator('validity')(
                <DatePicker
                  style={{ width: '100%' }}
                  format="DD-MM-YYYY"
                  disabled={isView}
                  placeholder="Select Validity Date"
                />
              )}
            </Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

const WrappedForm19Modal = Form.create({ name: 'form19_modal' })(Form19Modal);

export default WrappedForm19Modal;
